import React, { useState, useEffect } from 'react'
import { Button, Card, Col, Input, Row, Select, Table, Tag, Typography, Modal, List, Collapse } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash, faSearch, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import recipesData from '../utils/recipes.json'

const { Title, Text, Paragraph } = Typography
const { Option } = Select
const { Panel } = Collapse
const { Search } = Input

const RecipesManagement = ({ data, setData, dietaryPreferences }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredRecipes, setFilteredRecipes] = useState([])
  const [selectedRecipeId, setSelectedRecipeId] = useState(null)
  const [recipeModalVisible, setRecipeModalVisible] = useState(false)
  const [selectedRecipeDetails, setSelectedRecipeDetails] = useState(null)
  const [filterByDietaryPref, setFilterByDietaryPref] = useState(true)

  // Extract all dietary preferences
  const allDietaryTypes = dietaryPreferences.map((pref) => pref.dietType)
  const allDietaryRestrictions = dietaryPreferences.reduce((acc, pref) => {
    return [...acc, ...pref.restrictions]
  }, [])

  useEffect(() => {
    filterRecipes()
  }, [searchTerm, filterByDietaryPref, dietaryPreferences])

  const filterRecipes = () => {
    let results = [...recipesData.recipes]

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      results = results.filter(
        (recipe) =>
          recipe.name.toLowerCase().includes(term) ||
          recipe.description.toLowerCase().includes(term) ||
          recipe.tags.some((tag) => tag.toLowerCase().includes(term))
      )
    }

    // Filter by dietary preferences if enabled
    if (filterByDietaryPref && dietaryPreferences.length > 0) {
      results = results.filter((recipe) => {
        // Check if recipe suits any of the diet types (vegan, vegetarian, etc.)
        const matchesDietType = recipe.suitableFor.some((diet) => allDietaryTypes.includes(diet))

        // Check if recipe violates any restrictions
        const hasNoRestrictions = !recipe.ingredients.some((ingredient) =>
          allDietaryRestrictions.includes(ingredient.category)
        )

        return matchesDietType && hasNoRestrictions
      })
    }

    setFilteredRecipes(results)
  }

  const handleAddRecipe = () => {
    if (selectedRecipeId) {
      const recipeToAdd = recipesData.recipes.find((r) => r.id === selectedRecipeId)

      // Check if recipe is already added
      if (!data.some((r) => r.id === selectedRecipeId)) {
        setData([...data, recipeToAdd])
      }

      setSelectedRecipeId(null)
    }
  }

  const handleRemoveRecipe = (id) => {
    setData(data.filter((recipe) => recipe.id !== id))
  }

  const showRecipeDetails = (recipe) => {
    setSelectedRecipeDetails(recipe)
    setRecipeModalVisible(true)
  }

  const columns = [
    {
      title: 'Recipe Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => <a onClick={() => showRecipeDetails(record)}>{text}</a>
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Suitable For',
      dataIndex: 'suitableFor',
      key: 'suitableFor',
      render: (_, record) => (
        <span>
          {record.suitableFor.map((diet) => (
            <Tag color='green' key={diet}>
              {diet}
            </Tag>
          ))}
        </span>
      )
    },
    {
      title: 'Calories',
      dataIndex: 'nutritionalValue',
      key: 'calories',
      render: (nutritionalValue) => `${nutritionalValue.calories} kcal`
    },
    {
      title: 'Prep Time',
      dataIndex: 'prepTime',
      key: 'prepTime'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          danger
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => handleRemoveRecipe(record.id)}
          size='small'
        />
      )
    }
  ]

  return (
    <div>
      <Title level={4}>Recipes & Ingredients</Title>
      <Text>Select recipes that align with the meal plan's dietary preferences and nutritional goals.</Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Search
              placeholder='Search recipes by name, description, or tags'
              allowClear
              enterButton={<FontAwesomeIcon icon={faSearch} />}
              size='large'
              onSearch={(value) => setSearchTerm(value)}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>

          <Col span={24}>
            <Select
              showSearch
              placeholder='Select recipe to add'
              style={{ width: '100%' }}
              value={selectedRecipeId}
              onChange={(value) => setSelectedRecipeId(value)}
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {filteredRecipes.map((recipe) => (
                <Option key={recipe.id} value={recipe.id}>
                  {recipe.name} ({recipe.category}) - {recipe.nutritionalValue.calories} kcal
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                type='primary'
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={handleAddRecipe}
                disabled={!selectedRecipeId}
              >
                Add Recipe
              </Button>

              <div>
                <Text>Filter by dietary preferences: </Text>
                <Select defaultValue={true} style={{ width: 120 }} onChange={(value) => setFilterByDietaryPref(value)}>
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      <Table dataSource={data} columns={columns} rowKey='id' pagination={false} size='small' />

      <Modal
        title={selectedRecipeDetails?.name}
        open={recipeModalVisible}
        onCancel={() => setRecipeModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedRecipeDetails && (
          <div>
            <Paragraph>{selectedRecipeDetails.description}</Paragraph>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card title='Nutritional Information' size='small'>
                  <p>
                    <strong>Calories:</strong> {selectedRecipeDetails.nutritionalValue.calories} kcal
                  </p>
                  <p>
                    <strong>Protein:</strong> {selectedRecipeDetails.nutritionalValue.protein} g
                  </p>
                  <p>
                    <strong>Carbs:</strong> {selectedRecipeDetails.nutritionalValue.carbs} g
                  </p>
                  <p>
                    <strong>Fat:</strong> {selectedRecipeDetails.nutritionalValue.fat} g
                  </p>
                </Card>
              </Col>

              <Col span={12}>
                <Card title='Details' size='small'>
                  <p>
                    <strong>Prep Time:</strong> {selectedRecipeDetails.prepTime}
                  </p>
                  <p>
                    <strong>Cook Time:</strong> {selectedRecipeDetails.cookTime}
                  </p>
                  <p>
                    <strong>Servings:</strong> {selectedRecipeDetails.servings}
                  </p>
                  <p>
                    <strong>Suitable For:</strong>{' '}
                    {selectedRecipeDetails.suitableFor.map((diet) => (
                      <Tag color='green' key={diet}>
                        {diet}
                      </Tag>
                    ))}
                  </p>
                </Card>
              </Col>
            </Row>

            <Collapse defaultActiveKey={['1', '2']} style={{ marginTop: 16 }}>
              <Panel header='Ingredients' key='1'>
                <List
                  dataSource={selectedRecipeDetails.ingredients}
                  renderItem={(item) => (
                    <List.Item>
                      <Text>
                        {item.amount} {item.unit} {item.name}
                      </Text>
                    </List.Item>
                  )}
                />
              </Panel>
              <Panel header='Instructions' key='2'>
                <List
                  dataSource={selectedRecipeDetails.instructions}
                  renderItem={(item, index) => (
                    <List.Item>
                      <Text>
                        <strong>{index + 1}.</strong> {item}
                      </Text>
                    </List.Item>
                  )}
                />
              </Panel>
            </Collapse>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default RecipesManagement
